// The guide's `.sec` — one tour-page section.
//
// Deliberately NEITHER "use client" NOR "server-only". It is pure presentation
// with no state and no server API, and it has to be usable from both sides:
// the static sections render it on the server, while the departures list is a
// client component that needs the same frame. Marking it `server-only` broke
// the build the moment the client one imported it, and marking it
// `"use client"` would drag every section's JSX into the browser bundle.
//
// Sections are NOT cards. They sit on the panel with a single hairline between
// consecutive ones (`.sec + .sec { border-top }`), which is what makes a long
// page read as one document instead of a stack of boxes. Reaching for a card
// per section is the reflex to resist: nested cards inside a page that is
// already a two-column layout is what makes storefronts look assembled rather
// than designed.

import type { ReactNode } from "react";

import { cx } from "@tripcart/traveler-ui/cx";

import styles from "./_tour-section.module.css";

/**
 * What makes an element one of this page's sections. EXPORTED, because
 * several things draw a section and only one of them is this component:
 * itinerary, cost/reviews (`_static-sections`), the operator map, custom tabs,
 * the FAQ panel, the departures list and the streaming placeholder all put
 * this class on their own root.
 *
 * `.section + .section` (in `_tour-section.module.css`) draws the divider
 * between any two adjacent elements carrying this class, however they got it.
 * What breaks the chain is an element between two sections that carries none
 * of it — the deleted `#reviews-anchor` wrapper took out its own rule and the
 * following section's.
 *
 * The scroll offset is the sticky header (64px) plus the tab bar (~46px) plus
 * a gap — the same 124px the rail pins at, so a tab click and the rail agree
 * on where the top of the page is.
 *
 * The rule belongs to the FOLLOWING section, not the preceding one, so the
 * last section on the page never draws a trailing line into the footer.
 */
export const TOUR_SECTION_CLASS = styles.section;

/**
 * The section heading roles (`title` / `description` / `subTitle`) and their
 * spacing (`headingFlush` / `descriptionSpaced`), for the section renderers
 * that draw their own header instead of using `TourSection`. The roles carry
 * no margin, so a caller spaces its heading with its own class.
 */
export { styles as tourSectionStyles };

interface TourSectionProps {
  /** Anchor id — must match the tab slug that points at it. */
  id?: string;
  title: string;
  /** The line under the heading. Omitted renders nothing, never an empty p. */
  description?: string | null;
  /** Right-hand content in the heading row (a filter, a count, a link). When
   *  present the header becomes the guide's `.sec-head-row`. */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function TourSection({
  id,
  title,
  description,
  action,
  children,
  className,
}: TourSectionProps) {
  const heading = (
    <>
      <h2 className={cx(styles.title, styles.headingFlush)}>{title}</h2>
      {description ? (
        <p className={cx(styles.description, styles.descriptionSpaced)}>
          {description}
        </p>
      ) : null}
    </>
  );
  return (
    <section id={id} className={cx(TOUR_SECTION_CLASS, className)}>
      {action ? (
        <div className={cx(styles.header, styles.headerRow)}>
          <div className={styles.headerText}>{heading}</div>
          {action}
        </div>
      ) : (
        <div className={styles.header}>{heading}</div>
      )}
      {children}
    </section>
  );
}
